import React, { useEffect } from "react";
import { IconClose } from "./toolbarIcons";
import { resolveMediaUrl } from "./mediaUrl";

export function formatDrawerValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function normalizeField(field) {
  if (Array.isArray(field)) {
    return { label: field[0], value: field[1], full: Boolean(field[2]) };
  }
  return {
    label: field.label,
    value: field.value,
    full: Boolean(field.full)
  };
}

export function RecordDrawerPill({ children, variant = "neutral" }) {
  return <span className={`record-drawer__pill record-drawer__pill--${variant}`}>{children}</span>;
}

export function RecordDrawer({ open, onClose, ariaLabel, children, footer, wide = false }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="record-drawer-root" role="presentation">
      <button type="button" className="record-drawer__overlay" aria-label="Close details" onClick={onClose} />
      <aside
        className={`record-drawer${wide ? " record-drawer--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
        {footer ? <footer className="record-drawer__footer">{footer}</footer> : null}
      </aside>
    </div>
  );
}

export function RecordDrawerHeader({ label, title, subtitle, onClose, tags }) {
  return (
    <>
      <header className="record-drawer__header">
        <div className="record-drawer__header-main">
          {label ? <p className="record-drawer__label">{label}</p> : null}
          <h2>{title}</h2>
          {subtitle ? <p className="record-drawer__date">{subtitle}</p> : null}
        </div>
        <div className="record-drawer__header-actions">
          <button type="button" className="record-drawer__close" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>
      </header>
      {tags?.length ? (
        <div className="record-drawer__tags">
          {tags.map((tag) => (
            <RecordDrawerPill key={tag.key ?? tag.label} variant={tag.variant ?? "neutral"}>
              {tag.label}
            </RecordDrawerPill>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function RecordDrawerBody({ children }) {
  return <div className="record-drawer__body">{children}</div>;
}

export function RecordDrawerSection({ title, action, children }) {
  return (
    <section className="record-drawer__section">
      {title || action ? (
        <div className="record-drawer__section-head">
          {title ? <h3>{title}</h3> : <span />}
          {action ? <span className="record-drawer__section-action">{action}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function RecordDrawerFacts({ fields = [], columns = 1 }) {
  if (!fields.length) return null;
  return (
    <div className="record-drawer__facts-panel">
      <div className={`record-drawer__facts${columns > 1 ? " record-drawer__facts--split" : ""}`}>
        {fields.map((field) => {
          const { label, value, full } = normalizeField(field);
          return (
            <div key={label} className={full ? "record-drawer__fact record-drawer__fact--full" : "record-drawer__fact"}>
              <span className="record-drawer__fact-label">{label}</span>
              <span className="record-drawer__fact-value">{formatDrawerValue(value)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RecordDrawerImages({ images = [] }) {
  const visible = images.filter((img) => img?.src);
  if (!visible.length) return null;

  return (
    <div className="record-drawer__images">
      {visible.map((img) => {
        const src = resolveMediaUrl(img.src) ?? img.src;
        return (
          <div key={img.label ?? src} className="record-drawer__image-block">
            <span className="record-drawer__image-label">{img.label}</span>
            <img src={src} alt={img.alt ?? img.label ?? "Image"} className="record-drawer__image" />
            {img.href !== false ? (
              <a href={src} target="_blank" rel="noreferrer" className="record-drawer__link">
                {img.linkText ?? "Open image"}
              </a>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function RecordDrawerNote({ children }) {
  return <p className="record-drawer__note">{children}</p>;
}

/** Generic read-only detail drawer for list "View" actions. */
export function RecordViewDrawer({
  open,
  onClose,
  label = "Details",
  title,
  subtitle,
  tags = [],
  sections = [],
  images = [],
  footer,
  ariaLabel,
  wide = false,
  children
}) {
  return (
    <RecordDrawer open={open} onClose={onClose} ariaLabel={ariaLabel ?? title ?? label} footer={footer} wide={wide}>
      <RecordDrawerHeader label={label} title={title ?? "—"} subtitle={subtitle} onClose={onClose} tags={tags} />
      <RecordDrawerBody>
        {images.length > 0 ? (
          <RecordDrawerSection title="Images">
            <RecordDrawerImages images={images} />
          </RecordDrawerSection>
        ) : null}
        {children}
        {sections.map((section) => (
          <RecordDrawerSection key={section.title} title={section.title} action={section.action}>
            {section.note ? <RecordDrawerNote>{section.note}</RecordDrawerNote> : null}
            <RecordDrawerFacts fields={section.fields} columns={section.columns} />
            {section.content ?? null}
          </RecordDrawerSection>
        ))}
      </RecordDrawerBody>
    </RecordDrawer>
  );
}
